import { AlertTriangle, Check, DollarSign, ShieldCheck, Sparkles } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { CostQuoteInfo } from "@/lib/api";
import { formatUsd, summarizeParamValue } from "@/lib/format";
import { promptOf } from "@/lib/jobDetails";
import { MENTION_TEXT_FIELDS } from "@/lib/mentions";

interface Props {
  quote: CostQuoteInfo | null;
  open: boolean;
  loading: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: () => void;
  /** Offered when prompt checks fired and the model has an AI prompt rewrite. */
  onImprovePrompt?: () => void;
}

const usd = (value: string | number | null) => formatUsd(value) ?? "—";

export function CostConfirmationDialog({ quote, open, loading, error, onClose, onConfirm, onImprovePrompt }: Props) {
  const fullPrompt = promptOf({ params: quote?.params ?? {} });
  const params = Object.entries(quote?.params ?? {}).filter(
    ([key, value]) => !MENTION_TEXT_FIELDS.some((field) => field === key)
      && !["resolved_mentions", "reference_bindings", "creative_direction_resolved"].includes(key)
      && !key.endsWith("_manual") && value !== "" && value != null
  );
  const usage = Object.entries(quote?.cost.breakdown ?? {});
  const costRange = quote?.cost.min_usd !== quote?.cost.max_usd;

  return (
    <Dialog open={open} onClose={loading ? () => undefined : onClose} title="Review generation cost">
      {quote && (
        <>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5">
          {error && <p role="alert" className="mb-3 rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error}</p>}
          {(quote.prompt_checks?.length ?? 0) > 0 && (
            <div className="mb-4 rounded-xl border border-studio-warning-border bg-studio-warning-soft p-3 text-xs text-studio-warning-ink">
              <p className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle className="h-3.5 w-3.5" />
                Prompt check
              </p>
              <ul className="mt-1.5 list-disc space-y-1 pl-4 leading-5">
                {quote.prompt_checks?.map((check) => <li key={check.code}>{check.message}</li>)}
              </ul>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                {onImprovePrompt && (
                  <Button type="button" size="sm" variant="secondary" onClick={onImprovePrompt} disabled={loading}>
                    <Sparkles className="h-3.5 w-3.5" />
                    Fix prompt with AI
                  </Button>
                )}
                <span className="text-[11px] opacity-80">You can still generate with the prompt as written.</span>
              </div>
            </div>
          )}
          <div className="rounded-xl border border-studio-accent/25 bg-studio-accent-soft p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-studio-accent">
                  {(quote.generation_count ?? 1) > 1 ? `Total maximum · ${quote.generation_count} outputs` : "Maximum reservation"}
                </p>
                <p className="mt-1 font-mono text-2xl font-semibold tabular-nums text-studio-ink">
                  {usd(quote.cost.max_usd ?? quote.cost.estimated_usd)}
                </p>
                {costRange && (
                  <p className="mt-1 text-xs text-studio-muted">
                    Estimated range {usd(quote.cost.min_usd)}–{usd(quote.cost.max_usd)}
                  </p>
                )}
              </div>
              <span className="flex items-center gap-1.5 rounded-lg border border-studio-border bg-studio-panel px-2.5 py-1.5 font-mono text-xs font-semibold text-studio-ink">
                <DollarSign className="h-3.5 w-3.5 text-studio-accent" />
                USD
              </span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-xl border border-studio-border bg-studio-card p-3">
              <p className="text-studio-muted">Available balance</p>
              <p className="mt-1 font-mono font-semibold tabular-nums text-studio-ink">
                {usd(quote.wallet.available_usd)}
              </p>
            </div>
            <div className="rounded-xl border border-studio-border bg-studio-card p-3">
              <p className="text-studio-muted">Quote expires</p>
              <p className="mt-1 font-mono font-semibold tabular-nums text-studio-ink">
                {new Date(quote.expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          </div>

          {fullPrompt && (
            <details className="mt-5 rounded-xl border border-studio-border bg-studio-card p-3">
              <summary className="cursor-pointer text-xs font-semibold text-studio-ink">Full prompt</summary>
              <p className="mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap break-words text-xs leading-5 text-studio-muted">{fullPrompt}</p>
            </details>
          )}
          {params.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-studio-muted">
                Frozen settings
              </p>
              <dl className="divide-y divide-studio-border rounded-xl border border-studio-border bg-studio-card px-3">
                {params.map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between gap-4 py-2 text-xs">
                    <dt className="capitalize text-studio-muted">{key.replace(/_/g, " ")}</dt>
                    <dd className="max-w-[60%] truncate font-mono text-right text-studio-ink">
                      {summarizeParamValue(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {usage.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-studio-muted">Usage basis</p>
              <dl className="divide-y divide-studio-border rounded-xl border border-studio-border bg-studio-card px-3">
                {usage.map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between gap-4 py-2 text-xs">
                    <dt className="capitalize text-studio-muted">{key.replace(/_/g, " ")}</dt>
                    <dd className="font-mono text-right text-studio-ink">{summarizeParamValue(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {quote.wallet.sufficient ? (
              <p className="flex items-start gap-2 text-xs leading-5 text-studio-success">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                Balance check passed. The provider request starts only after confirmation.
              </p>
            ) : (
              <p className="flex items-start gap-2 text-xs leading-5 text-studio-danger">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Insufficient USD balance or member spending limit for this reservation.
              </p>
            )}
            {quote.warnings.map((warning) => (
              <p key={warning} className="text-[11px] leading-4 text-studio-muted">{warning}</p>
            ))}
          </div>

          </div>
          <div className="grid shrink-0 grid-cols-1 gap-2 border-t border-studio-border p-4 sm:flex sm:justify-end sm:px-5">
            <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="button" onClick={onConfirm} loading={loading} disabled={!quote.wallet.sufficient}>
              {!loading && <Check className="h-4 w-4" />}
              Confirm and generate
            </Button>
          </div>
        </>
      )}
    </Dialog>
  );
}
